import { getAttributionSubId } from '../lib/attribution'

const PARTNER_URL = 'https://partner.tcgplayer.com/MKKbJ2'

import { isBasicLand } from '../lib/basicLands'

export { isBasicLand }

/**
 * Generate a TCGPlayer affiliate link for a single card.
 * Uses direct product page if tcgplayerId is available, otherwise falls back to search.
 */
export function tcgCardUrl(cardName: string, tcgplayerId?: number | null): string {
  const dest = tcgplayerId
    ? `https://www.tcgplayer.com/product/${tcgplayerId}`
    // eslint-disable-next-line i18next/no-literal-string -- URL
    : `https://www.tcgplayer.com/search/magic/product?productLineName=magic&q=${encodeURIComponent(cardName)}&view=grid`
  // eslint-disable-next-line i18next/no-literal-string -- URL
  return `${PARTNER_URL}?u=${encodeURIComponent(dest)}&subId1=${encodeURIComponent(getAttributionSubId())}`
}

export interface BulkBuyCard {
  name: string
  tcgplayerId?: number | null
  quantity?: number
}

/**
 * The name form TCGPlayer mass entry resolves, or null if there isn't one.
 *
 * Mass entry matches EXACTLY against TCGPlayer's own product name, and their
 * naming for two-faced cards does not follow Scryfall's layout: measured
 * against the live site 2026-08-31, `split` resolves as the full "A // B"
 * while transform / modal DFC / adventure / prepare resolve by front face
 * only, and flip cards disagree with each other. No rule derived from our data
 * gets every one right, so any name carrying " // " gets no name form and is
 * dropped from the cart (see isTcgRepresentable for why not a product id).
 *
 * A blank name has no name form either - "1 " is unresolvable, and ONE
 * unresolvable entry blocks the whole cart.
 */
export function massEntryName(name: string | null | undefined): string | null {
  const n = (name ?? '').trim()
  if (!n || n.includes(' // ')) return null
  return n
}

/** Normalized copy count. Missing means 1; unparseable means null. */
function quantityOf(card: BulkBuyCard): number | null {
  if (card.quantity == null) return 1
  return Number.isFinite(card.quantity) ? Math.trunc(card.quantity) : null
}

/**
 * Whether mass entry can carry this row at all. Shared by the cart builder and
 * the "what can this vendor sell" prefilter - a prefilter looser than the
 * builder would advertise cards the cart silently drops.
 *
 * A row needs a NAME form. Product ids are deliberately not a fallback:
 * measured against the live site 2026-08-31, a single `quantity-productId`
 * entry whose printing has thin stock fails the WHOLE cart with "we were
 * unable to automatically add the items to your cart" once the cart is more
 * than ~20 rows. Same 50-name cart, one extra id entry: product 457193
 * resolved, product 222103 killed it. That is the original defect, and one
 * two-faced card would have re-armed it for nearly every real deck. Dropping
 * the row instead costs one card and is disclosed by BuyDeckCard's "only N of
 * those are available" line.
 */
export function isTcgRepresentable(card: BulkBuyCard): boolean {
  if (isBasicLand(card.name ?? '')) return false
  const qty = quantityOf(card)
  if (qty == null || qty <= 0) return false
  return massEntryName(card.name) != null
}

/**
 * Generate a TCGPlayer mass entry URL with pre-filled cards.
 *
 * EVERY row goes in by name so TCGPlayer picks the printing and finds stock
 * itself. A tcgplayerId from our Scryfall bulk pins ONE arbitrary printing,
 * and 3.4% of those are unpriced or far above the cheapest, which rejected
 * real carts. Rows with no usable name are dropped, not pinned - see
 * isTcgRepresentable.
 */
export function tcgBulkBuyUrl(cards: BulkBuyCard[]): string {
  const entries = cards
    .filter(isTcgRepresentable)
    .map(card => `${quantityOf(card) ?? 1} ${massEntryName(card.name)}`)
  // The `c` value is encoded BEFORE it enters the destination: names carry
  // spaces and "&" ("Don & Raph, Hard Science"), and a raw "&" would end the
  // query parameter and truncate the card name.
  const dest = `https://www.tcgplayer.com/massentry?productline=Magic&c=${encodeURIComponent(entries.join('||'))}`
  // eslint-disable-next-line i18next/no-literal-string -- URL
  return `${PARTNER_URL}?u=${encodeURIComponent(dest)}&subId1=${encodeURIComponent(getAttributionSubId())}`
}
